import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Swords, Coins, Users, ChevronRight, LogOut, Wallet, Link2 as LinkIcon } from "lucide-react";
import { auth, api, getToken, getStoredUser } from "../lib/api";
import AuthForm from "../components/AuthForm";
import WalletModal from "../components/WalletModal";
import ChallengeBell from "../components/ChallengeBell";
import { GuestLock, GuestUnlockModal } from "../components/GuestUnlock";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";

const STAKES = [10, 25, 50, 100];

const Multiplayer = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState(getStoredUser());
  const [stake, setStake] = useState(10);
  const [oppName, setOppName] = useState("");
  const [incoming, setIncoming] = useState([]);
  const [showWallet, setShowWallet] = useState(false);
  const [creating, setCreating] = useState(false);
  const [linking, setLinking] = useState(false);
  const [showUnlock, setShowUnlock] = useState(false);


  useEffect(() => {
    auth.ensure().then(setUser).catch(() => setUser(null));
    const load = () => api.incomingMatches().then(setIncoming).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const logout = () => {
    auth.logout().then(() => auth.guest()).then(setUser).catch(() => setUser(null));
  };

  const doCreateChallenge = async (u) => {
    const name = oppName.trim();
    if (!u.is_guest && (u.coins ?? 0) < stake) {
      toast({ title: "Not enough balance", description: `You need £${stake} to set this stake.` });
      return;
    }
    if ((u.games_left_today ?? 10) <= 0) {
      toast({ title: "Daily limit reached", description: "You've used all 10 wager games today — come back tomorrow!" });
      return;
    }
    setCreating(true);
    try {
      const res = await api.createMatch(stake, name);
      setUser((prev) => ({ ...prev, coins: res.coins, games_left_today: Math.max(0, (prev.games_left_today ?? 10) - 1) }));
      navigate(`/multiplayer/match/${res.match.id}`);
    } catch (e) {
      toast({ title: "Couldn't create challenge", description: e?.response?.data?.detail || "Try again" });
    } finally {
      setCreating(false);
    }
  };

  const doCreateLink = async (u) => {
    if (!u.is_guest && (u.coins ?? 0) < stake) {
      toast({ title: "Not enough balance", description: `You need £${stake} to set this stake.` });
      return;
    }
    if ((u.games_left_today ?? 10) <= 0) {
      toast({ title: "Daily limit reached", description: "You've used all 10 wager games today — come back tomorrow!" });
      return;
    }
    setLinking(true);
    try {
      const res = await api.createMatch(stake, "");
      setUser((prev) => ({ ...prev, coins: res.coins, games_left_today: Math.max(0, (prev.games_left_today ?? 10) - 1) }));
      navigate(`/multiplayer/match/${res.match.id}`);
    } catch (e) {
      toast({ title: "Couldn't create link", description: e?.response?.data?.detail || "Try again" });
    } finally {
      setLinking(false);
    }
  };

  // No sign-up needed to start a wager; guests are asked to sign up before seeing results.
  const gate = (action) => action(user);

  const createChallenge = () => {
    if (!oppName.trim()) {
      toast({ title: "Enter a username", description: "Type the mate you want to challenge, or use a shareable link below." });
      return;
    }
    gate(doCreateChallenge);
  };

  const createLink = () => gate(doCreateLink);

  return (
    <div className="bg-app min-h-screen text-white relative overflow-hidden">
      <div className="absolute inset-0 hero-glow pointer-events-none" />

      <div className="relative z-10 max-w-lg mx-auto px-5 h-16 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="pill-nav rounded-full px-3.5 py-1.5 flex items-center gap-2 text-sm font-medium text-slate-200"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="font-display font-extrabold flex items-center gap-1.5 tracking-tight">
          <Swords className="w-4 h-4 text-[#10B981]" />
          <span className="text-white">FOOTIE</span>
          <span className="text-[#38BDF8]">CLASH</span>
        </div>
        <div className="flex items-center justify-end w-16">
          <ChallengeBell key={user?.email || "anon"} />
        </div>
      </div>

      <div className="relative z-10 max-w-lg mx-auto px-5 py-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#10B981]/15 border border-[#10B981]/30 mb-4">
            <Users className="w-7 h-7 text-[#10B981]" />
          </div>
          <h1 className="font-display font-extrabold text-3xl">Head-to-Head</h1>
          <p className="text-slate-400 mt-2 text-sm">
            Stake virtual £, join a live lobby, then draft head-to-head on a split screen — highest points takes the pot.
          </p>
        </div>

        {!user ? (
          <AuthForm onAuthed={setUser} title="Sign up to wager a mate" subtitle="Create your free account and get £30 virtual to start." />
        ) : (
          <>
            {/* wallet */}
            <div className="card-38 p-4 flex items-center justify-between mb-5 hover:translate-y-0">
              <div className="min-w-0">
                <div className="text-muted-38 text-xs">Signed in as</div>
                <div className="font-display font-bold text-lg truncate">{user.username}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 rounded-xl bg-[#10B981]/12 border border-[#10B981]/30 px-3 py-2">
                  <Coins className="w-5 h-5 text-[#10B981]" />
                  <span className="font-display font-bold text-lg text-[#10B981]">£{(user.coins ?? 0).toLocaleString()}</span>
                  <GuestLock guest={user.is_guest} className="w-4 h-4" />
                </div>
                <button
                  onClick={() => setShowWallet(true)}
                  title="Top up or withdraw"
                  className="rounded-xl bg-white/5 border border-white/10 p-2.5 hover:border-[#10B981]/50 transition-colors"
                >
                  <Wallet className="w-5 h-5 text-slate-200" />
                </button>
                <button onClick={logout} title="Log out" className="text-slate-500 hover:text-white transition-colors">
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* incoming challenges */}
            {incoming.length > 0 && (
              <div className="card-38 p-5 mb-5 hover:translate-y-0">
                <div className="font-semibold text-white mb-3">Challenges waiting for you</div>
                <div className="space-y-2">
                  {incoming.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => navigate(`/multiplayer/match/${m.id}`)}
                      className="w-full flex items-center justify-between gap-3 rounded-xl border border-[#38BDF8]/30 bg-[#38BDF8]/10 px-4 py-3 text-left hover:bg-[#38BDF8]/15 transition-colors"
                    >
                      <div className="min-w-0">
                        <div className="text-white font-semibold text-sm truncate">{m.creator.username} challenged you</div>
                        <div className="text-muted-38 text-xs">Stake {m.stake} · Winner takes {m.pot}</div>
                      </div>
                      <span className="rounded-lg bg-[#38BDF8] text-white text-sm font-semibold px-4 py-1.5 shrink-0">Accept</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* create wager: shareable link first, username challenge second */}
            <div className="card-38 p-5 mb-5 hover:translate-y-0">
              <div className="font-semibold text-white mb-1">Wager a mate</div>
              <div className="text-muted-38 text-xs mb-3">Pick a stake, send the link and you're both straight into the draft.</div>
              <div className="grid grid-cols-4 gap-2 mb-4">
                {STAKES.map((s) => (
                  <button
                    key={s}
                    data-testid={`stake-${s}`}
                    onClick={() => (user.is_guest ? setShowUnlock(true) : setStake(s))}
                    className={`rounded-xl py-3 font-display font-bold text-sm border transition-colors ${
                      !user.is_guest && stake === s
                        ? "bg-[#10B981] text-[#0F172A] border-[#10B981]"
                        : "bg-white/5 text-slate-200 border-white/10 hover:border-white/25"
                    }`}
                  >
                    £{s}
                  </button>
                ))}
              </div>
              <button
                data-testid="create-link-btn"
                onClick={createLink}
                disabled={linking}
                className="btn-green wager-glow w-full rounded-xl py-4 font-extrabold text-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <LinkIcon className="w-5 h-5" />
                {linking ? "Creating link…" : "Get my wager link"}
              </button>
              {user.is_guest && (
                <p data-testid="guest-friendly-note" className="text-[#38BDF8] text-xs mt-2 text-center">Your balance is locked as a guest, so links are free friendlies. Sign up to unlock them and stake.</p>
              )}
              <p className="text-slate-500 text-xs mt-2 text-center">
                {user.is_guest ? "Free friendly" : `Winner takes the £${stake * 2} pot`} · {user.games_left_today ?? 10}/10 wager games left today
              </p>

              <div className="flex items-center gap-3 my-4">
                <div className="h-px flex-1 bg-white/10" />
                <span className="text-muted-38 text-xs">or challenge by username</span>
                <div className="h-px flex-1 bg-white/10" />
              </div>

              <div className="flex gap-2">
                <input
                  data-testid="opponent-username-input"
                  value={oppName}
                  onChange={(e) => setOppName(e.target.value)}
                  placeholder="Their username"
                  maxLength={20}
                  className="flex-1 min-w-0 rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-[#10B981]/60"
                />
                <button
                  data-testid="create-challenge-btn"
                  onClick={createChallenge}
                  disabled={creating}
                  className="btn-ghost rounded-xl px-4 py-3 font-semibold text-white flex items-center gap-1 disabled:opacity-50 shrink-0"
                >
                  {creating ? "…" : <>Challenge <ChevronRight className="w-4 h-4" /></>}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {user && (
        <WalletModal
          open={showWallet}
          onClose={() => setShowWallet(false)}
          coins={user.coins}
          onUpdated={(coins) => setUser((u) => ({ ...u, coins }))}
        />
      )}
      <GuestUnlockModal open={showUnlock} onClose={() => setShowUnlock(false)} onAuthed={setUser} coins={user?.coins} />
      <Toaster />
    </div>
  );
};

export default Multiplayer;
